import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { createPcmPlayerNode } from '../../utils/pcmPlayerWorklet';

const MAX_RECONNECT_DELAY_MS = 10000;
const MIN_PLAYOUT_DELAY_MS = 120;
const INITIAL_PLAYOUT_DELAY_MS = 180;
const STEADY_PLAYOUT_DELAY_MS = 180;
const BURST_PLAYOUT_DELAY_MS = 280;
const MAX_PLAYOUT_DELAY_MS = 360;
const MAX_QUEUE_FRAMES = 90;
const CATCH_UP_QUEUE_FRAMES = 24;
const ENVELOPE_HEADER_LEN = 32;
const NATIVE_CODEC_H264 = 1;
const NATIVE_CODEC_G711A = 2;
const NATIVE_CODEC_H265 = 3;
const NATIVE_CODEC_PCM16 = 4;
const AUDIO_SAMPLE_RATE = 8000;
const STARTUP_GREEN_WARNING_THRESHOLD = 32;

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const findNalUnits = (u8) => {
  const starts = [];
  for (let i = 0; i < u8.length - 3; i += 1) {
    if (u8[i] === 0 && u8[i + 1] === 0 && u8[i + 2] === 0 && u8[i + 3] === 1) {
      starts.push([i, 4]);
      i += 3;
    } else if (u8[i] === 0 && u8[i + 1] === 0 && u8[i + 2] === 1) {
      starts.push([i, 3]);
      i += 2;
    }
  }
  return starts.map(([pos, len], idx) => {
    const start = pos + len;
    const end = idx + 1 < starts.length ? starts[idx + 1][0] : u8.length;
    return u8.subarray(start, end);
  }).filter((nal) => nal.length > 0);
};

const h264NalTypes = (u8) => findNalUnits(u8).map((nal) => nal[0] & 0x1f);
const h265NalTypes = (u8) => findNalUnits(u8)
  .filter((nal) => nal.length >= 2)
  .map((nal) => (nal[0] >> 1) & 0x3f);
const nalTypesForCodec = (u8, nativeCodec = NATIVE_CODEC_H264) => (
  nativeCodec === NATIVE_CODEC_H265 ? h265NalTypes(u8) : h264NalTypes(u8)
);
const hasIdr = (u8) => h264NalTypes(u8).includes(5);
const hasHevcKeyframe = (u8) => h265NalTypes(u8).some((type) => [19, 20, 21].includes(type));
const getSps = (u8) => findNalUnits(u8).find((nal) => (nal[0] & 0x1f) === 7);

const decodeALawSample = (value) => {
  const a = value ^ 0x55;
  let sample = ((a & 0x0f) << 4) + 8;
  const segment = (a & 0x70) >> 4;
  if (segment >= 1) sample += 0x100;
  if (segment > 1) sample <<= (segment - 1);
  return (a & 0x80 ? sample : -sample) / 32768;
};

const decodeALawToFloat32 = (payload) => {
  const pcm = new Float32Array(payload.length);
  for (let i = 0; i < payload.length; i += 1) {
    pcm[i] = decodeALawSample(payload[i]);
  }
  return pcm;
};

const decodePcm16LeToFloat32 = (payload) => {
  const samples = Math.floor(payload.length / 2);
  const pcm = new Float32Array(samples);
  for (let i = 0; i < samples; i += 1) {
    const lo = payload[i * 2];
    const hi = payload[i * 2 + 1];
    const value = (hi & 0x80) ? ((hi << 8) | lo) - 0x10000 : ((hi << 8) | lo);
    pcm[i] = Math.max(-1, Math.min(1, value / 32768));
  }
  return pcm;
};

const isLikelySolidGreenStartupFrame = (frame) => {
  if (typeof document === 'undefined') return false;
  try {
    const sampleWidth = 24;
    const sampleHeight = 14;
    const sampleCanvas = document.createElement('canvas');
    sampleCanvas.width = sampleWidth;
    sampleCanvas.height = sampleHeight;
    const sampleCtx = sampleCanvas.getContext('2d', { willReadFrequently: true });
    if (!sampleCtx) return false;
    sampleCtx.drawImage(frame, 0, 0, sampleWidth, sampleHeight);
    const pixels = sampleCtx.getImageData(0, 0, sampleWidth, sampleHeight).data;
    let greenDominant = 0;
    let brightEnough = 0;
    let totalGreen = 0;
    let totalRed = 0;
    let totalBlue = 0;
    const totalPixels = sampleWidth * sampleHeight;
    for (let idx = 0; idx < pixels.length; idx += 4) {
      const red = pixels[idx];
      const green = pixels[idx + 1];
      const blue = pixels[idx + 2];
      totalRed += red;
      totalGreen += green;
      totalBlue += blue;
      if (green > 45) brightEnough += 1;
      if (green > red * 1.35 && green > blue * 1.25 && green - Math.max(red, blue) > 25) {
        greenDominant += 1;
      }
    }
    const greenRatio = greenDominant / totalPixels;
    const brightRatio = brightEnough / totalPixels;
    const avgGreen = totalGreen / totalPixels;
    const avgRed = totalRed / totalPixels;
    const avgBlue = totalBlue / totalPixels;
    return greenRatio > 0.82 && brightRatio > 0.75 && avgGreen > avgRed * 1.45 && avgGreen > avgBlue * 1.3;
  } catch (_) {
    return false;
  }
};

const readUint64 = (view, offset) => {
  const hi = view.getUint32(offset, false);
  const lo = view.getUint32(offset + 4, false);
  return hi * 4294967296 + lo;
};

const parseNativeFrame = (buffer) => {
  const u8 = new Uint8Array(buffer);
  if (
    u8.length >= ENVELOPE_HEADER_LEN
    && u8[0] === 69 && u8[1] === 76 && u8[2] === 86 && u8[3] === 49
  ) {
    const view = new DataView(buffer, u8.byteOffset, u8.byteLength);
    const payloadLength = view.getUint32(22, false);
    const payloadStart = ENVELOPE_HEADER_LEN;
    const payloadEnd = payloadStart + payloadLength;
    if (payloadLength > 0 && payloadEnd <= u8.length) {
      return {
        payload: u8.subarray(payloadStart, payloadEnd),
        metadata: {
          version: view.getUint8(4),
          channel: view.getUint8(5),
          codec: view.getUint8(6),
          dataType: view.getUint8(7),
          isKeyframe: Boolean(view.getUint8(8) & 1),
          isPrimer: Boolean(view.getUint8(8) & 2),
          segmentation: view.getUint8(9),
          timestampMs: readUint64(view, 10),
          previousFrameIntervalMs: view.getUint32(18, false),
          payloadLength,
          sequenceNumber: view.getUint32(26, false),
          previousIframeIntervalMs: view.getUint16(30, false),
          envelope: true,
        },
      };
    }
  }
  return {
    payload: u8,
    metadata: {
      version: 0,
      channel: 0,
      codec: 1,
      dataType: 255,
      isKeyframe: hasIdr(u8),
      isPrimer: false,
      segmentation: 0,
      timestampMs: null,
      previousFrameIntervalMs: 0,
      payloadLength: u8.length,
      sequenceNumber: 0,
      previousIframeIntervalMs: 0,
      envelope: false,
    },
  };
};

const codecFromSps = (sps) => {
  if (!sps || sps.length < 4) return 'avc1.42E01E';
  return `avc1.${[sps[1], sps[2], sps[3]]
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase()}`;
};

const hevcCodecCandidates = () => [
  'hev1.1.6.L93.B0',
  'hvc1.1.6.L93.B0',
  'hev1.1.6.L120.B0',
  'hvc1.1.6.L120.B0',
];

const decoderConfigForCodec = (codec, nativeCodec) => {
  if (nativeCodec === NATIVE_CODEC_H265) return { codec };
  return { codec, avc: { format: 'annexb' } };
};

const NativeWebCodecsPlayer = React.forwardRef(function NativeWebCodecsPlayer(
  { wsUrl, wsUrlFactory, onStatusChange, onAudioDetected, onAudioStatusChange, onFirstVisibleFrame, label, startupText = 'Starting live video' },
  ref,
) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const wsRef = useRef(null);
  const decoderRef = useRef(null);
  const frameQueueRef = useRef([]);
  const playoutTimerRef = useRef(null);
  const playoutBaseRef = useRef(null);
  const playoutDelayRef = useRef(INITIAL_PLAYOUT_DELAY_MS);
  const jitterEwmaRef = useRef(0);
  const lastArrivalMsRef = useRef(null);
  const lastStreamTsMsRef = useRef(null);
  const decodingFrameRef = useRef(false);
  const suppressedTimestampsRef = useRef(new Set());
  const reconnectTimerRef = useRef(null);
  const reconnectDelayRef = useRef(500);
  const closedByUnmountRef = useRef(false);
  const audioContextRef = useRef(null);
  const audioGainRef = useRef(null);
  const audioWorkletRef = useRef(null);
  const audioWorkletReadyRef = useRef(null);
  const mutedRef = useRef(true);
  const configuredCodecRef = useRef(null);
  const configuredNativeCodecRef = useRef(null);
  const sawKeyframeRef = useRef(false);
  const baseTimeMsRef = useRef(null);
  const metadataBaseTsMsRef = useRef(null);
  const lastTimestampUsRef = useRef(0);
  const hasVisibleFrameRef = useRef(false);
  const startupGreenSuppressionsRef = useRef(0);
  const inputSampleRef = useRef({ at: performance.now(), frames: 0, decoded: 0, displayed: 0 });
  const statsRef = useRef({
    frames: 0,
    decoded: 0,
    displayed: 0,
    dropped: 0,
    droppedLate: 0,
    bytes: 0,
    idr: 0,
    keyChunks: 0,
    deltaChunks: 0,
    primerChunks: 0,
    unsupportedSps: 0,
    codec: '—',
    decoderState: 'new',
    lastNalTypes: '—',
    envelope: 'raw',
    queueDepth: 0,
    playoutDelayMs: INITIAL_PLAYOUT_DELAY_MS,
    adaptiveMode: 'steady',
    jitterMs: 0,
    catchupDrops: 0,
    frameIntervalMs: 0,
    audioFrames: 0,
    audioBytes: 0,
    audioQueuedMs: 0,
    audioDrops: 0,
    audioUnsupported: 0,
    audioPayloadType: 0,
    audioCodec: '—',
    audioState: 'muted',
    inputFps: 0,
    decodedFps: 0,
    displayFps: 0,
    startupGreenFrames: 0,
    lastError: '',
  });

  const [status, setStatus] = useState('connecting');
  const [errorText, setErrorText] = useState('');
  const [stats, setStats] = useState(statsRef.current);
  const [hasVisibleFrame, setHasVisibleFrame] = useState(false);
  const [copyState, setCopyState] = useState('idle');

  const debugMode = React.useMemo(() => {
    if (typeof window === 'undefined') return false;
    try { return new URLSearchParams(window.location.search).get('debug') === '1'; } catch (_) { return false; }
  }, []);

  const updateStatus = useCallback((next) => {
    setStatus(next);
    onStatusChange?.(next);
  }, [onStatusChange]);

  const drawFrame = useCallback((frame) => {
    const canvas = canvasRef.current;
    if (!canvas) {
      frame.close();
      return;
    }
    const frameTimestamp = frame.timestamp;
    if (suppressedTimestampsRef.current.has(frameTimestamp)) {
      suppressedTimestampsRef.current.delete(frameTimestamp);
      frame.close();
      statsRef.current = { ...statsRef.current, decoded: statsRef.current.decoded + 1 };
      return;
    }
    const width = frame.displayWidth || frame.codedWidth || 640;
    const height = frame.displayHeight || frame.codedHeight || 360;
    if (!hasVisibleFrameRef.current && isLikelySolidGreenStartupFrame(frame)) {
      const nextSuppressionCount = startupGreenSuppressionsRef.current + 1;
      startupGreenSuppressionsRef.current = nextSuppressionCount;
      frame.close();
      statsRef.current = {
        ...statsRef.current,
        decoded: statsRef.current.decoded + 1,
        dropped: statsRef.current.dropped + 1,
        startupGreenFrames: statsRef.current.startupGreenFrames + 1,
        lastError: nextSuppressionCount >= STARTUP_GREEN_WARNING_THRESHOLD
          ? `still suppressing green startup frames (${nextSuppressionCount})`
          : 'suppressed green startup frame',
      };
      return;
    }
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
    }
    const ctx = canvas.getContext('2d');
    ctx.drawImage(frame, 0, 0, width, height);
    frame.close();
    if (!hasVisibleFrameRef.current) {
      hasVisibleFrameRef.current = true;
      setHasVisibleFrame(true);
      onFirstVisibleFrame?.();
    }
    statsRef.current = {
      ...statsRef.current,
      decoded: statsRef.current.decoded + 1,
      displayed: statsRef.current.displayed + 1,
    };
    updateStatus('playing');
  }, [onFirstVisibleFrame, updateStatus]);

  const teardown = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    try { wsRef.current?.close(); } catch (_) { /* noop */ }
    wsRef.current = null;
    try { decoderRef.current?.close(); } catch (_) { /* noop */ }
    decoderRef.current = null;
    try { audioWorkletRef.current?.port.postMessage({ type: 'reset' }); } catch (_) { /* noop */ }
    try { audioWorkletRef.current?.disconnect(); } catch (_) { /* noop */ }
    audioWorkletRef.current = null;
    audioWorkletReadyRef.current = null;
    try { audioContextRef.current?.close(); } catch (_) { /* noop */ }
    audioContextRef.current = null;
    audioGainRef.current = null;
    if (playoutTimerRef.current) {
      clearTimeout(playoutTimerRef.current);
      playoutTimerRef.current = null;
    }
    frameQueueRef.current = [];
    playoutBaseRef.current = null;
    playoutDelayRef.current = INITIAL_PLAYOUT_DELAY_MS;
    jitterEwmaRef.current = 0;
    lastArrivalMsRef.current = null;
    lastStreamTsMsRef.current = null;
    decodingFrameRef.current = false;
    suppressedTimestampsRef.current.clear();
    configuredCodecRef.current = null;
    configuredNativeCodecRef.current = null;
    sawKeyframeRef.current = false;
    baseTimeMsRef.current = null;
    metadataBaseTsMsRef.current = null;
    lastTimestampUsRef.current = 0;
    hasVisibleFrameRef.current = false;
    startupGreenSuppressionsRef.current = 0;
    setHasVisibleFrame(false);
  }, []);

  const ensureAudioGraph = useCallback(async () => {
    if (typeof window === 'undefined') return null;
    const AudioCtor = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtor) return null;
    if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
      const context = new AudioCtor();
      const gain = context.createGain();
      gain.gain.value = mutedRef.current ? 0 : 1;
      gain.connect(context.destination);
      audioContextRef.current = context;
      audioGainRef.current = gain;
      audioWorkletRef.current = null;
      audioWorkletReadyRef.current = null;
    }
    if (!audioWorkletRef.current) {
      if (!audioWorkletReadyRef.current) {
        const context = audioContextRef.current;
        audioWorkletReadyRef.current = createPcmPlayerNode(context)
          .then((node) => {
            if (!node || audioContextRef.current !== context) return null;
            node.connect(audioGainRef.current);
            node.port.onmessage = (event) => {
              const msg = event?.data;
              if (msg?.type !== 'level') return;
              statsRef.current = {
                ...statsRef.current,
                audioQueuedMs: msg.bufferedMs,
                audioState: mutedRef.current ? 'muted' : (msg.playing ? 'active' : 'buffering'),
              };
            };
            audioWorkletRef.current = node;
            return node;
          })
          .catch(() => {
            statsRef.current = { ...statsRef.current, audioState: 'worklet-unsupported' };
            return null;
          });
      }
      await audioWorkletReadyRef.current;
    }
    if (!mutedRef.current && audioContextRef.current.state === 'suspended') {
      try { await audioContextRef.current.resume(); } catch (_) { /* user gesture may be required */ }
    }
    return audioContextRef.current;
  }, []);

  const scheduleDecodedAudio = useCallback(async (payload, decodeFn, codecLabel, payloadType = 0) => {
    onAudioDetected?.(true);
    statsRef.current = {
      ...statsRef.current,
      audioFrames: statsRef.current.audioFrames + 1,
      audioBytes: statsRef.current.audioBytes + payload.length,
      audioPayloadType: payloadType,
      audioCodec: codecLabel,
      audioState: mutedRef.current ? 'muted' : 'active',
    };
    if (mutedRef.current) return;

    const context = await ensureAudioGraph();
    const worklet = audioWorkletRef.current;
    if (!context || !worklet || context.state === 'suspended') {
      statsRef.current = { ...statsRef.current, audioDrops: statsRef.current.audioDrops + 1 };
      return;
    }

    try {
      const pcm = decodeFn(payload);
      if (pcm.length) {
        worklet.port.postMessage(
          { type: 'push', samples: pcm, rate: AUDIO_SAMPLE_RATE },
          [pcm.buffer],
        );
      }
    } catch (err) {
      statsRef.current = {
        ...statsRef.current,
        audioDrops: statsRef.current.audioDrops + 1,
        audioState: 'error',
        lastError: err?.message || 'audio decode failed',
      };
    }
  }, [ensureAudioGraph, onAudioDetected]);

  const schedulePcmaAudio = useCallback((payload, payloadType = 0) => {
    return scheduleDecodedAudio(payload, decodeALawToFloat32, `PCMA${payloadType ? `/PT${payloadType}` : ''}`, payloadType);
  }, [scheduleDecodedAudio]);

  const schedulePcm16Audio = useCallback((payload, payloadType = 0) => {
    const source = Number(payloadType) === 19 ? 'AAC→PCM16' : 'G726→PCM16';
    return scheduleDecodedAudio(payload, decodePcm16LeToFloat32, `${source}${payloadType ? `/PT${payloadType}` : ''}`, payloadType);
  }, [scheduleDecodedAudio]);

  const isCodecSupported = async (codec, nativeCodec) => {
    const config = decoderConfigForCodec(codec, nativeCodec);
    if (typeof VideoDecoder.isConfigSupported !== 'function') return true;
    try {
      const support = await VideoDecoder.isConfigSupported(config);
      return Boolean(support?.supported);
    } catch (_) {
      return false;
    }
  };

  const configureDecoder = useCallback(async (nativeCodec, payload) => {
    if (decoderRef.current?.state === 'configured' && configuredNativeCodecRef.current === nativeCodec) return true;
    if (typeof VideoDecoder === 'undefined') {
      setErrorText('WebCodecs is not supported in this browser');
      updateStatus('error');
      return false;
    }

    const candidates = [];
    let primaryCodec = 'avc1.42E01E';
    if (nativeCodec === NATIVE_CODEC_H265) {
      candidates.push(...hevcCodecCandidates());
      primaryCodec = candidates[0];
    } else {
      const sps = getSps(payload);
      const spsCodec = codecFromSps(sps);
      if (sps) candidates.push(spsCodec);
      candidates.push('avc1.42E01E');
      primaryCodec = spsCodec;
    }

    let codec = null;
    for (const candidate of [...new Set(candidates)]) {
      // Some MD300 chunks can contain byte patterns that look like SPS but
      // produce unsupported strings such as avc1.7A53AF. Treat those as data,
      // not a reason to destroy an already viable decoder path.

      if (await isCodecSupported(candidate, nativeCodec)) {
        codec = candidate;
        break;
      }
      if (nativeCodec === NATIVE_CODEC_H264 && candidate === primaryCodec) {
        statsRef.current = {
          ...statsRef.current,
          unsupportedSps: statsRef.current.unsupportedSps + 1,
          lastError: `ignored unsupported SPS codec ${candidate}`,
        };
      }
    }
    if (!codec) {
      setErrorText(nativeCodec === NATIVE_CODEC_H265
        ? 'This browser/device cannot decode H.265 video. Use a supported browser/device or switch the device stream to H.264.'
        : `WebCodecs cannot decode ${primaryCodec}`);
      updateStatus('error');
      return false;
    }

    try { decoderRef.current?.close(); } catch (_) { /* noop */ }
    const decoder = new VideoDecoder({
      output: drawFrame,
      error: (err) => {

        console.warn('[NativeWebCodecsPlayer] decoder error:', err?.message || err);
        try { decoderRef.current?.close(); } catch (_) { /* noop */ }
        decoderRef.current = null;
        configuredCodecRef.current = null;
        configuredNativeCodecRef.current = null;
        sawKeyframeRef.current = false;
        frameQueueRef.current = [];
        playoutBaseRef.current = null;
        statsRef.current = {
          ...statsRef.current,
          decoderState: 'closed',
          lastError: `${err?.message || 'Decoder error'}; waiting for next keyframe`,
        };
        updateStatus('connecting');
      },
    });
    const config = decoderConfigForCodec(codec, nativeCodec);
    decoder.configure(config);
    decoderRef.current = decoder;
    configuredCodecRef.current = codec;
    configuredNativeCodecRef.current = nativeCodec;
    statsRef.current = { ...statsRef.current, codec, decoderState: decoder.state, lastError: '' };
    return true;
  }, [drawFrame, updateStatus]);

  const timestampUs = () => {
    const now = performance.now();
    if (baseTimeMsRef.current == null) baseTimeMsRef.current = now;
    let ts = Math.round((now - baseTimeMsRef.current) * 1000);
    if (ts <= lastTimestampUsRef.current) ts = lastTimestampUsRef.current + 1;
    lastTimestampUsRef.current = ts;
    return ts;
  };

  const metadataTimestampUs = (timestampMs) => {
    if (timestampMs == null) return timestampUs();
    if (metadataBaseTsMsRef.current == null) metadataBaseTsMsRef.current = timestampMs;
    let ts = Math.round((timestampMs - metadataBaseTsMsRef.current) * 1000);
    if (ts < 0) ts = timestampUs();
    if (ts <= lastTimestampUsRef.current) ts = lastTimestampUsRef.current + 1;
    lastTimestampUsRef.current = ts;
    return ts;
  };

  const refreshRateStats = () => {
    const now = performance.now();
    const sample = inputSampleRef.current;
    const elapsed = now - sample.at;
    if (elapsed < 1000) return;
    const scale = 1000 / elapsed;
    statsRef.current = {
      ...statsRef.current,
      inputFps: Math.round((statsRef.current.frames - sample.frames) * scale * 10) / 10,
      decodedFps: Math.round((statsRef.current.decoded - sample.decoded) * scale * 10) / 10,
      displayFps: Math.round((statsRef.current.displayed - sample.displayed) * scale * 10) / 10,
    };
    inputSampleRef.current = {
      at: now,
      frames: statsRef.current.frames,
      decoded: statsRef.current.decoded,
      displayed: statsRef.current.displayed,
    };
  };

  const updateAdaptiveDelay = (frame) => {
    if (frame.metadata.isPrimer) {
      statsRef.current = {
        ...statsRef.current,
        playoutDelayMs: Math.round(playoutDelayRef.current),
        adaptiveMode: 'primer',
      };
      return;
    }

    let observedJitterMs = jitterEwmaRef.current;
    if (lastArrivalMsRef.current != null) {
      const arrivalDeltaMs = frame.arrivalMs - lastArrivalMsRef.current;
      let streamDeltaMs = 0;
      if (frame.metadata.timestampMs != null && lastStreamTsMsRef.current != null) {
        streamDeltaMs = frame.metadata.timestampMs - lastStreamTsMsRef.current;
      }
      if (!streamDeltaMs && frame.metadata.previousFrameIntervalMs) {
        streamDeltaMs = frame.metadata.previousFrameIntervalMs;
      }
      if (streamDeltaMs > 0 && streamDeltaMs < 2000 && arrivalDeltaMs > 0) {
        const instantJitter = Math.abs(arrivalDeltaMs - streamDeltaMs);
        observedJitterMs = jitterEwmaRef.current === 0
          ? instantJitter
          : (jitterEwmaRef.current * 0.82) + (instantJitter * 0.18);
        jitterEwmaRef.current = observedJitterMs;
      }
    }
    lastArrivalMsRef.current = frame.arrivalMs;
    if (frame.metadata.timestampMs != null) lastStreamTsMsRef.current = frame.metadata.timestampMs;

    const queueDepth = frameQueueRef.current.length;
    let targetDelay = STEADY_PLAYOUT_DELAY_MS;
    let adaptiveMode = 'steady';
    if (queueDepth >= 16 || observedJitterMs >= 110) {
      targetDelay = MAX_PLAYOUT_DELAY_MS;
      adaptiveMode = 'burst';
    } else if (queueDepth >= 8 || observedJitterMs >= 55) {
      targetDelay = BURST_PLAYOUT_DELAY_MS;
      adaptiveMode = 'smooth';
    } else if (queueDepth <= 3 && observedJitterMs <= 25) {
      targetDelay = MIN_PLAYOUT_DELAY_MS;
      adaptiveMode = 'low-latency';
    }

    const current = playoutDelayRef.current;
    const next = targetDelay > current
      ? (current * 0.65) + (targetDelay * 0.35)
      : (current * 0.9) + (targetDelay * 0.1);
    playoutDelayRef.current = clamp(next, MIN_PLAYOUT_DELAY_MS, MAX_PLAYOUT_DELAY_MS);
    statsRef.current = {
      ...statsRef.current,
      playoutDelayMs: Math.round(playoutDelayRef.current),
      adaptiveMode,
      jitterMs: Math.round(observedJitterMs),
      queueDepth,
    };
  };

  const catchUpToNextKeyframe = useCallback((reason) => {
    const queue = frameQueueRef.current;
    const nextKeyIndex = queue.findIndex((queued, idx) => idx > 0 && queued.key);
    if (nextKeyIndex <= 0) return false;
    frameQueueRef.current = queue.slice(nextKeyIndex);
    try { decoderRef.current?.reset(); } catch (_) { /* noop */ }
    playoutBaseRef.current = null;
    metadataBaseTsMsRef.current = null;
    lastTimestampUsRef.current = 0;
    suppressedTimestampsRef.current.clear();
    sawKeyframeRef.current = true;
    statsRef.current = {
      ...statsRef.current,
      dropped: statsRef.current.dropped + nextKeyIndex,
      droppedLate: statsRef.current.droppedLate + nextKeyIndex,
      catchupDrops: statsRef.current.catchupDrops + nextKeyIndex,
      queueDepth: frameQueueRef.current.length,
      adaptiveMode: reason,
      lastError: `caught up to next keyframe (${nextKeyIndex} stale frames skipped)`,
    };
    return true;
  }, []);

  const schedulePlayout = useCallback(() => {
    if (playoutTimerRef.current || closedByUnmountRef.current) return;
    playoutTimerRef.current = setTimeout(async () => {
      playoutTimerRef.current = null;
      if (decodingFrameRef.current || closedByUnmountRef.current) {
        schedulePlayout();
        return;
      }
      decodingFrameRef.current = true;
      try {
        const now = performance.now();
        let nextDelay = 8;
        while (frameQueueRef.current.length > 0) {
          const frame = frameQueueRef.current[0];
          if (!playoutBaseRef.current) {
            playoutBaseRef.current = {
              wallMs: now + playoutDelayRef.current,
              timestampMs: frame.metadata.timestampMs ?? now,
              arrivalMs: frame.arrivalMs,
            };
          }
          const base = playoutBaseRef.current;
          const frameDeltaMs = frame.metadata.isPrimer
            ? 0
            : (frame.metadata.timestampMs == null
              ? Math.max(0, frame.arrivalMs - base.arrivalMs)
              : Math.max(0, frame.metadata.timestampMs - base.timestampMs));
          const dueMs = frame.metadata.isPrimer ? now : base.wallMs + frameDeltaMs;
          const lateMs = frame.metadata.isPrimer ? 0 : now - dueMs;
          if (!frame.metadata.isPrimer && lateMs > 700 && frameQueueRef.current.length >= 8) {
            if (catchUpToNextKeyframe('catch-up')) {
              continue;
            }
          }
          if (lateMs < -4) {
            nextDelay = Math.max(4, Math.min(40, dueMs - now));
            break;
          }

          frameQueueRef.current.shift();
          const configured = await configureDecoder(frame.metadata.codec, frame.payload);
          if (!configured || decoderRef.current?.state !== 'configured') continue;
          try {
            const chunkTimestamp = metadataTimestampUs(frame.metadata.timestampMs);
            if (frame.metadata.isPrimer) {
              suppressedTimestampsRef.current.add(chunkTimestamp);
              playoutBaseRef.current = null;
            }
            decoderRef.current.decode(new EncodedVideoChunk({
              type: frame.key ? 'key' : 'delta',
              timestamp: chunkTimestamp,
              data: frame.payload,
            }));
          } catch (err) {
            statsRef.current = {
              ...statsRef.current,
              dropped: statsRef.current.dropped + 1,
              lastError: err?.message || 'decode failed',
              decoderState: decoderRef.current?.state || 'error',
            };
            if (frame.key) {
              try { decoderRef.current?.reset(); } catch (_) { /* noop */ }
              sawKeyframeRef.current = false;
              frameQueueRef.current = [];
              playoutBaseRef.current = null;
            suppressedTimestampsRef.current.clear();
              break;
            }
          }
          statsRef.current = {
            ...statsRef.current,
            queueDepth: frameQueueRef.current.length,
            jitterMs: Math.max(statsRef.current.jitterMs, Math.round(Math.max(0, lateMs))),
          };
        }
        if (frameQueueRef.current.length > 0) {
          playoutTimerRef.current = setTimeout(() => {
            playoutTimerRef.current = null;
            schedulePlayout();
          }, nextDelay);
        }
      } finally {
        decodingFrameRef.current = false;
      }
    }, 0);
  // metadataTimestampUs is an inline, ref-based timestamp helper in the imperative
  // WebCodecs decode/playout engine; adding it (and the useCallback chain it would
  // require) would needlessly regenerate this playout scheduler. Intentional.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configureDecoder, catchUpToNextKeyframe]);

  const enqueueFrame = useCallback((frame) => {
    frameQueueRef.current.push(frame);
    frameQueueRef.current.sort((a, b) => {
      const at = a.metadata.timestampMs ?? a.arrivalMs;
      const bt = b.metadata.timestampMs ?? b.arrivalMs;
      if (at !== bt) return at - bt;
      return a.metadata.sequenceNumber - b.metadata.sequenceNumber;
    });
    if (frameQueueRef.current.length > MAX_QUEUE_FRAMES) {
      catchUpToNextKeyframe('catch-up');
    } else if (frameQueueRef.current.length >= CATCH_UP_QUEUE_FRAMES) {
      catchUpToNextKeyframe('catch-up');
    }
    updateAdaptiveDelay(frame);
    statsRef.current = { ...statsRef.current, queueDepth: frameQueueRef.current.length };
    schedulePlayout();
  }, [schedulePlayout, catchUpToNextKeyframe]);

  const scheduleReconnect = useCallback((connectFn) => {
    if (closedByUnmountRef.current) return;
    updateStatus('retrying');
    if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
    const delay = reconnectDelayRef.current;
    reconnectTimerRef.current = setTimeout(connectFn, delay);
    reconnectDelayRef.current = Math.min(delay * 2, MAX_RECONNECT_DELAY_MS);
  }, [updateStatus]);

  const connect = useCallback(async () => {
    if (!wsUrl && !wsUrlFactory) return;
    try { wsRef.current?.close(); } catch (_) { /* noop */ }
    wsRef.current = null;
    updateStatus('connecting');
    setErrorText('');

    let ws;
    try {
      const resolvedWsUrl = wsUrlFactory ? await wsUrlFactory() : wsUrl;
      if (!resolvedWsUrl) throw new Error('Stream token unavailable');
      ws = new WebSocket(resolvedWsUrl);
      ws.binaryType = 'arraybuffer';
    } catch (err) {
      setErrorText(err?.message || 'WebSocket open failed');
      scheduleReconnect(connect);
      return;
    }
    wsRef.current = ws;

    ws.onopen = () => updateStatus('connecting');
    ws.onmessage = async (event) => {
      if (typeof event.data === 'string') {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === 'error') scheduleReconnect(connect);
        } catch (_) { /* noop */ }
        return;
      }
      const { payload, metadata } = parseNativeFrame(event.data);
      const audioPayloadType = metadata.dataType === 3 ? metadata.previousIframeIntervalMs : 0;
      if (metadata.codec === NATIVE_CODEC_G711A) {
        schedulePcmaAudio(payload, audioPayloadType);
        refreshRateStats();
        return;
      }
      if (metadata.codec === NATIVE_CODEC_PCM16) {
        schedulePcm16Audio(payload, audioPayloadType);
        refreshRateStats();
        return;
      }
      if (metadata.dataType === 3) {
        statsRef.current = {
          ...statsRef.current,
          audioFrames: statsRef.current.audioFrames + 1,
          audioBytes: statsRef.current.audioBytes + payload.length,
          audioUnsupported: statsRef.current.audioUnsupported + 1,
          audioPayloadType,
          audioCodec: `unsupported-pt${audioPayloadType || '?'}-codec${metadata.codec}`,
          audioState: 'unsupported',
          lastError: `ignored unsupported audio pt=${audioPayloadType || '?'} codec=${metadata.codec}`,
        };
        refreshRateStats();
        return;
      }
      if (![NATIVE_CODEC_H264, NATIVE_CODEC_H265].includes(metadata.codec)) {
        statsRef.current = {
          ...statsRef.current,
          dropped: statsRef.current.dropped + 1,
          lastError: `ignored unsupported native codec ${metadata.codec}`,
        };
        return;
      }
      const types = nalTypesForCodec(payload, metadata.codec);
      const key = metadata.isKeyframe || (metadata.codec === NATIVE_CODEC_H265 ? hasHevcKeyframe(payload) : types.includes(5));
      const previousInterval = metadata.previousFrameIntervalMs || 0;
      statsRef.current = {
        ...statsRef.current,
        frames: statsRef.current.frames + 1,
        bytes: statsRef.current.bytes + payload.length,
        idr: statsRef.current.idr + (key ? 1 : 0),
        keyChunks: statsRef.current.keyChunks + (key ? 1 : 0),
        deltaChunks: statsRef.current.deltaChunks + (key ? 0 : 1),
        lastNalTypes: `${metadata.codec === NATIVE_CODEC_H265 ? 'h265:' : 'h264:'}${types.slice(0, 16).join(',') || 'none'}`,
        decoderState: decoderRef.current?.state || 'new',
        envelope: metadata.envelope ? `v${metadata.version}` : 'raw',
        frameIntervalMs: previousInterval,
        primerChunks: statsRef.current.primerChunks + (metadata.isPrimer ? 1 : 0),
      };
      refreshRateStats();
      if (key) sawKeyframeRef.current = true;
      if (!sawKeyframeRef.current) {
        statsRef.current = { ...statsRef.current, dropped: statsRef.current.dropped + 1 };
        return;
      }
      enqueueFrame({ payload, metadata, key, arrivalMs: performance.now() });
    };
    ws.onerror = () => scheduleReconnect(connect);
    ws.onclose = (ev) => {
      if (closedByUnmountRef.current) return;
      if (ev?.code === 4401) {
        setErrorText('Stream token missing');
        updateStatus('error');
        return;
      }
      if (ev?.code === 4403) {
        setErrorText('Stream token rejected');
        updateStatus('error');
        return;
      }
      scheduleReconnect(connect);
    };
  }, [wsUrl, wsUrlFactory, updateStatus, scheduleReconnect, enqueueFrame, schedulePcmaAudio, schedulePcm16Audio]);

  useEffect(() => {
    closedByUnmountRef.current = false;
    reconnectDelayRef.current = 500;
    connect();
    return () => {
      closedByUnmountRef.current = true;
      teardown();
    };
  }, [connect, teardown]);

  useEffect(() => {
    const interval = setInterval(() => {
      const next = { ...statsRef.current };
      setStats(next);
      onAudioStatusChange?.({
        codec: next.audioCodec,
        payloadType: next.audioPayloadType,
        frames: next.audioFrames,
        bytes: next.audioBytes,
        unsupported: next.audioUnsupported,
        drops: next.audioDrops,
        state: next.audioState,
      });
    }, 500);
    return () => clearInterval(interval);
  }, [onAudioStatusChange]);

  React.useImperativeHandle(ref, () => ({
    captureFrameSnapshot: async () => {
      const canvas = canvasRef.current;
      if (!canvas || !canvas.width || !canvas.height) return null;
      try {
        if (typeof canvas.toBlob === 'function' && typeof URL !== 'undefined') {
          const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.82));
          if (blob) return URL.createObjectURL(blob);
        }
        if (typeof canvas.toDataURL === 'function') {
          return canvas.toDataURL('image/jpeg', 0.82);
        }
      } catch (_) {
        return null;
      }
      return null;
    },
    setMuted: async (muted) => {
      mutedRef.current = muted;
      if (audioGainRef.current) audioGainRef.current.gain.value = muted ? 0 : 1;
      if (muted) {
        // Flush buffered samples so unmuting doesn't replay stale audio.
        try { audioWorkletRef.current?.port.postMessage({ type: 'reset' }); } catch (_) { /* noop */ }
      }
      if (!muted) await ensureAudioGraph();
      statsRef.current = {
        ...statsRef.current,
        audioState: muted ? 'muted' : (audioContextRef.current?.state || 'active'),
      };
    },
    isMuted: () => mutedRef.current,
    goFullscreen: () => {
      const el = containerRef.current;
      if (el?.requestFullscreen) el.requestFullscreen();
      else if (el?.webkitRequestFullscreen) el.webkitRequestFullscreen();
    },
  }));

  const buildDebugSnapshot = useCallback(() => ({
    captured_at: new Date().toISOString(),
    label: label || null,
    status,
    visible_frame: hasVisibleFrame,
    debug_url: typeof window !== 'undefined' ? window.location.href : null,
    stats: {
      envelope: stats.envelope,
      adaptive_mode: stats.adaptiveMode,
      codec: stats.codec,
      decoder_state: stats.decoderState,
      input_fps: stats.inputFps,
      decoded_fps: stats.decodedFps,
      display_fps: stats.displayFps,
      queue_depth: stats.queueDepth,
      playout_delay_ms: stats.playoutDelayMs,
      jitter_ms: stats.jitterMs,
      frame_interval_ms: stats.frameIntervalMs,
      frames: stats.frames,
      decoded: stats.decoded,
      displayed: stats.displayed,
      dropped: stats.dropped,
      dropped_late: stats.droppedLate,
      catchup_drops: stats.catchupDrops,
      key_chunks: stats.keyChunks,
      delta_chunks: stats.deltaChunks,
      primer_chunks: stats.primerChunks,
      idr: stats.idr,
      unsupported_sps: stats.unsupportedSps,
      startup_green_suppressed: stats.startupGreenFrames,
      audio_codec: stats.audioCodec,
      audio_payload_type: stats.audioPayloadType || null,
      audio_frames: stats.audioFrames,
      audio_queued_ms: stats.audioQueuedMs,
      audio_drops: stats.audioDrops,
      audio_unsupported: stats.audioUnsupported,
      audio_state: stats.audioState,
      audio_muted: mutedRef.current,
      last_nal_types: stats.lastNalTypes,
      bytes: stats.bytes,
      last_error: stats.lastError,
    },
  }), [hasVisibleFrame, label, stats, status]);

  const copyDebugSnapshot = useCallback(async () => {
    const snapshot = buildDebugSnapshot();
    try {
      await navigator.clipboard.writeText(JSON.stringify(snapshot, null, 2));
      setCopyState('copied');
      setTimeout(() => setCopyState('idle'), 1600);
    } catch (_) {
      setCopyState('failed');
      setTimeout(() => setCopyState('idle'), 1600);
    }
  }, [buildDebugSnapshot]);

  return (
    <div ref={containerRef} className="relative w-full h-full bg-black" data-testid="native-webcodecs-player">
      <canvas
        ref={canvasRef}
        className={`w-full h-full object-contain transition-opacity duration-200 ${hasVisibleFrame ? 'opacity-100' : 'opacity-0'}`}
        data-testid="native-webcodecs-canvas"
      />
      {status !== 'error' && !hasVisibleFrame && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/70 pointer-events-none" data-testid="native-webcodecs-connecting-overlay">
          <div className="text-center text-white">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
            <p className="text-sm" data-testid="native-webcodecs-startup-text">{startupText}{label ? ` · ${label}` : ''}…</p>
            <p className="text-xs text-white/50 mt-1">Waiting for first clean frame</p>
          </div>
        </div>
      )}
      {status === 'error' && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/70 pointer-events-none" data-testid="native-webcodecs-error-overlay">
          <div className="text-center text-white px-4">
            <p className="text-sm text-red-400 font-medium">WebCodecs unavailable</p>
            <p className="text-xs text-white/60 mt-1">{errorText || 'Decoder could not start'}</p>
          </div>
        </div>
      )}
      {debugMode && (
        <div className="absolute bottom-2 left-2 z-30 max-w-[72%] rounded bg-black/80 px-2 py-1 text-[10px] leading-tight text-cyan-300 font-mono" data-testid="native-webcodecs-debug">
          <button
            type="button"
            onClick={copyDebugSnapshot}
            className="pointer-events-auto mb-1 rounded border border-cyan-300/40 bg-cyan-300/10 px-2 py-0.5 text-[10px] font-semibold text-cyan-100 hover:bg-cyan-300/20 transition-colors"
            data-testid="native-webcodecs-copy-debug"
          >
            {copyState === 'copied' ? 'Copied' : copyState === 'failed' ? 'Copy failed' : 'Copy debug snapshot'}
          </button>
          <pre className="whitespace-pre-wrap m-0">
            {`status: ${status} envelope=${stats.envelope} mode=${stats.adaptiveMode}\ncodec:  ${stats.codec} decoder=${stats.decoderState}\nfps:    in=${stats.inputFps} dec=${stats.decodedFps} out=${stats.displayFps}\nqueue:  ${stats.queueDepth} delay=${stats.playoutDelayMs}ms jitter=${stats.jitterMs}ms int=${stats.frameIntervalMs}ms\nframes: ${stats.frames} decoded=${stats.decoded} shown=${stats.displayed} dropped=${stats.dropped}/${stats.droppedLate} catchup=${stats.catchupDrops}\nkey/delta: ${stats.keyChunks}/${stats.deltaChunks} primer=${stats.primerChunks} idr=${stats.idr} badSps=${stats.unsupportedSps}\nstartup: greenSuppressed=${stats.startupGreenFrames} warningAt=${STARTUP_GREEN_WARNING_THRESHOLD}\naudio:  ${stats.audioCodec} pt=${stats.audioPayloadType || '—'} frames=${stats.audioFrames} queued=${stats.audioQueuedMs}ms drops=${stats.audioDrops} unsup=${stats.audioUnsupported} state=${stats.audioState} muted=${mutedRef.current ? 'yes' : 'no'}\nnals:   ${stats.lastNalTypes}\nbytes:  ${stats.bytes}${stats.lastError ? `\ninfo:   ${stats.lastError}` : ''}`}
          </pre>
        </div>
      )}
    </div>
  );
});

export default NativeWebCodecsPlayer;
